import { useState } from 'react';
import { Zap, Plus, CheckCircle, Info } from 'lucide-react';
import { api } from '../services/api';

export default function QuickEntry() {
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!question.trim() || !answer.trim()) {
      alert('Please enter both question and answer');
      return;
    }

    setLoading(true);
    try {
      await api.createQuickEntry(question.trim(), answer.trim());
      setSuccess(true);
      setQuestion('');
      setAnswer('');

      // Reset success message after 3 seconds
      setTimeout(() => setSuccess(false), 3000);
    } catch (error: any) {
      console.error('Failed to create quick entry:', error);
      alert(error.response?.data?.error || 'Failed to add Q&A. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Quick Entry</h2>
        <p className="text-gray-600">
          Manually add question and answer pairs to your knowledge base. Perfect for FAQs or specific information not on your website.
        </p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900 mb-1">When to use Quick Entry?</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Frequently asked questions not documented on your website</li>
              <li>• Specific pricing, policy, or process information</li>
              <li>• Filling knowledge gaps identified in the "Knowledge Gaps" tab</li>
              <li>• Temporary information like holiday hours or special announcements</li>
            </ul>
          </div>
        </div>
      </div>

      {success && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <div>
              <h4 className="font-medium text-green-900">Q&A Added Successfully!</h4>
              <p className="text-sm text-green-700">Your AI can now answer this question with confidence.</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="question" className="block text-sm font-medium text-gray-700 mb-2">
              Question
            </label>
            <input
              id="question"
              type="text"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="e.g., What are your business hours?"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
              required
            />
            <p className="mt-1 text-sm text-gray-500">
              Enter the question as customers might ask it
            </p>
          </div>

          <div>
            <label htmlFor="answer" className="block text-sm font-medium text-gray-700 mb-2">
              Answer
            </label>
            <textarea
              id="answer"
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              placeholder="e.g., We're open Monday through Friday from 9 AM to 5 PM EST. We're closed on weekends and major holidays."
              rows={6}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent resize-none"
              required
            />
            <p className="mt-1 text-sm text-gray-500">
              Provide a clear, complete answer. The AI will use this exact text when responding to similar questions.
            </p>
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Zap className="w-4 h-4 text-yellow-500" />
              <span>This will be instantly available to your AI assistant</span>
            </div>
            <button
              type="submit"
              disabled={loading || !question.trim() || !answer.trim()}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-black to-black text-white rounded-lg font-medium hover:from-gray-800 hover:to-gray-800 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-5 h-5" />
              {loading ? 'Adding...' : 'Add Q&A'}
            </button>
          </div>
        </form>
      </div>

      <div className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="font-medium text-gray-900 mb-2">Best Practices</h4>
        <ul className="text-sm text-gray-700 space-y-2">
          <li className="flex items-start gap-2">
            <span className="text-black font-bold">•</span>
            <span><strong>Be specific:</strong> Clear, detailed answers work better than vague ones</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-black font-bold">•</span>
            <span><strong>Use natural language:</strong> Write how you'd explain it to a customer in conversation</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-black font-bold">•</span>
            <span><strong>Include variations:</strong> If a question can be phrased multiple ways, add separate entries for each</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-black font-bold">•</span>
            <span><strong>Keep it updated:</strong> Review and update answers as your policies or information changes</span>
          </li>
        </ul>
      </div>
    </div>
  );
}
